import { Button } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { WrenchIcon } from "@nens/ranalib";

import { getAssetPropertiesDisplayValue } from "../data/labelLookupTable";
import {
  getCurrentAssetProperty,
  getExistingTimedStructureControls,
} from "../state/existingEvents";
import { WaterLevelChart, getDemValue, getWaterLevelChart } from "../state/selectedAsset";
import { getTime } from "../state/simulation";
import { Asset, Breach, isPotentialBreach } from "../types/assets";
import { isValidData } from "../utils/isValidData";

import { ASSET_PROPERTIES, EditType, PropertyInformation } from "./AssetProperties";
import CollapsableSidebarStyles from "./CollapsableSidebar.module.css";
import CrossSectionGraph from "./cross-section-graph";
import EditBreach from "./EditBreach";
import { EditPropertyInfos } from "./EditPropertyInfos";
import menuStyles from "./Menu.module.css";
import { PropertyTableHeader } from "./PropertyTableHeader";
import RightMenuHeader from "./RightMenuHeader";
import assetInspectorStyles from "./SelectedAssetInspector.module.css";
import TimeseriesBreachCharts from "./TimeseriesBreachCharts";
import TimeseriesChart from "./TimeseriesChart";
import TimeseriesFlowChart from "./TimeseriesFlowChart";
import TimeseriesPumpDischargeChart from "./TimeseriesPumpDischargeChart";

interface Props {
  asset: Asset;
}

const AssetHeader: React.FC<{ asset: Asset }> = ({ asset }) => {
  const modelType = asset.properties.model_type;

  switch (modelType) {
    case "breaches":
      return <FormattedMessage id="oneDElementInspector.breach" defaultMessage="Breach" />;
    case "channels":
      return <FormattedMessage id="oneDElementInspector.channel" defaultMessage="Channel" />;
    case "culverts":
      return <FormattedMessage id="oneDElementInspector.culvert" defaultMessage="Culvert" />;
    case "levees":
      return <FormattedMessage id="oneDElementInspector.levee" defaultMessage="Levee" />;
    case "manholes":
      return <FormattedMessage id="oneDElementInspector.manhole" defaultMessage="Manhole" />;
    case "nodes":
      return <FormattedMessage id="oneDElementInspector.node" defaultMessage="Node" />;
    case "orifices":
      return <FormattedMessage id="oneDElementInspector.orifices" defaultMessage="Orifices" />;
    case "pipes":
      return <FormattedMessage id="oneDElementInspector.pipe" defaultMessage="Pipe" />;
    case "pumps":
      return (
        <FormattedMessage id="oneDElementInspector.pumpstation" defaultMessage="Pumpstation" />
      );
    case "weirs":
      return <FormattedMessage id="oneDElementInspector.weir" defaultMessage="Weir" />;
    default:
      // Should be unreachable
      return <FormattedMessage id="oneDElementInspector.header" defaultMessage="1D Element" />;
  }
};

const getWaterdepthGraph = (
  asset: Asset,
  waterTimeseriesGraph: WaterLevelChart,
  demValue: number | null
) => {
  if (
    (asset.properties.model_type === "manholes" || asset.properties.model_type === "nodes") &&
    asset.properties &&
    asset.properties.bottom_level &&
    typeof asset.properties.bottom_level === "number"
  ) {
    const bottomLevel = asset.properties.bottom_level || 0;
    const updatedGraph = waterTimeseriesGraph
      .map((item) => {
        return {
          ...item,
          value: item.value - bottomLevel,
        };
      })
      .map((item) => {
        if (item.value > 0) {
          return item;
        } else {
          return { ...item, value: NaN };
        }
      });
    return updatedGraph;
  } else if (
    asset.properties.model_type === "nodes" &&
    demValue !== null
    // others?
  ) {
    const updatedGraph = waterTimeseriesGraph
      .map((item) => {
        return {
          ...item,
          value: item.value - demValue,
        };
      })
      .map((item) => {
        if (item.value > 0) {
          return item;
        } else {
          return { ...item, value: NaN };
        }
      });
    return updatedGraph;
  } else {
    // In this case we do not know what to show thus show nothing
    return [];
  }
};

const ShowPropertyInfo: React.FC<{
  asset: Asset;
  propertyInfo: PropertyInformation;
  edit?: EditType;
}> = ({ asset, propertyInfo, edit }) => {
  const { prettyName: PrettyName, property, transformValue, unit } = propertyInfo;

  const existingTimedStructureControls = useSelector(getExistingTimedStructureControls);
  const time = useSelector(getTime);

  const currentValue = getCurrentAssetProperty(
    asset,
    existingTimedStructureControls,
    time,
    property
  );

  const displayValue = getAssetPropertiesDisplayValue(
    asset.properties.model_type + "",
    property + "",
    currentValue + ""
  );

  // Any property with a NoData value will not be shown in the GUI
  if (!isValidData(currentValue)) {
    return null;
  }

  const usedValue = transformValue ? transformValue(currentValue) : currentValue;

  const showValue = `${
    displayValue || (typeof usedValue === "number" ? Math.round(usedValue * 100) / 100 : usedValue)
  } ${unit ?? ""}`;
  return (
    <div
      key={property}
      className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
    >
      <span>
        {edit === "openclose" ? (
          <FormattedMessage id="oneDElementInspector.status" defaultMessage="Status" />
        ) : PrettyName ? (
          <PrettyName />
        ) : (
          property
        )}
      </span>
      {edit === "openclose" ? (
        usedValue === 0 ? (
          <span className={assetInspectorStyles.Closed}>Closed</span>
        ) : (
          <span className={assetInspectorStyles.Open}>Open</span>
        )
      ) : (
        // displayValue can be a meaningful value.
        // It shows the name of the structure property, instead of the technical (int) value.
        // For example a string value meaningful to the user, instead of the technical int values.
        // If displayValue is undefined then the technical value is used
        <span title={showValue}>{showValue}</span>
      )}
    </div>
  );
};

const SelectedAssetInspector: React.FC<Props> = ({ asset }) => {
  const waterTimeseriesGraph = useSelector(getWaterLevelChart);
  const demValue = useSelector(getDemValue);

  const [editing, setEditing] = useState<boolean>(false);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);

  // Close the editing menu each time a new asset is selected
  useEffect(() => setEditing(false), [asset.properties.site_id]);

  if (!ASSET_PROPERTIES) return null; // For TS

  const propertyInfo = ASSET_PROPERTIES[asset.properties.model_type] || [];
  const propertiesAlways = propertyInfo.filter((pi) => pi.show.includes("always"));
  const propertiesHidden = propertyInfo.filter((pi) => pi.show.includes("hidden"));
  const propertiesEdit = propertyInfo.filter((pi) => pi.show.includes("edit"));

  return (
    <div
      className={`${CollapsableSidebarStyles.LeftMenu} ${CollapsableSidebarStyles.LeftMenuPadChildDivs}`}
    >
      <RightMenuHeader titleElement={<AssetHeader asset={asset} />} parent={"ASSET"} />

      {/* Properties that are always displayed */}
      {propertiesAlways.length > 0 ? (
        <div className={`${assetInspectorStyles.DetailList}`}>
          {propertiesAlways.map((propertyInfo) => (
            <ShowPropertyInfo
              key={propertyInfo.property}
              asset={asset}
              propertyInfo={propertyInfo}
            />
          ))}
        </div>
      ) : null}

      {isPotentialBreach(asset) ? (
        <EditBreach potentialBreach={asset as Breach} editing={editing} setEditing={setEditing} />
      ) : null}

      {/* Editable properties */}

      {propertiesEdit.length > 0 && asset.properties.model_type !== "breaches" ? (
        <div>
          <PropertyTableHeader
            button={
              <Button
                variant="primary"
                onClick={() => {
                  setEditing(true);
                }}
                isDisabled={editing}
                leftIcon={<WrenchIcon />}
              >
                <FormattedMessage id="oneDElementInspector.edit" defaultMessage="Edit" />
              </Button>
            }
          >
            <FormattedMessage id="oneDElementInspector.settingsHeader" defaultMessage="Settings" />
          </PropertyTableHeader>

          {editing ? (
            <EditPropertyInfos
              asset={asset}
              propertyInfos={propertiesEdit}
              close={() => setEditing(false)}
            />
          ) : (
            <div className={`${assetInspectorStyles.DetailList}`}>
              {propertiesEdit.map((propertyInfo) => {
                return (
                  propertyInfo.edits &&
                  propertyInfo.edits.map((edit) => (
                    <ShowPropertyInfo
                      key={propertyInfo.property + edit}
                      asset={asset}
                      propertyInfo={propertyInfo}
                      edit={edit}
                    />
                  ))
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {/* Initially hidden "detail" properties */}
      {propertiesHidden.length > 0 ? (
        <div className={menuStyles.List}>
          <PropertyTableHeader
            button={
              detailsOpen ? (
                <Button variant="primary" onClick={() => setDetailsOpen(false)}>
                  <FormattedMessage id="oneDElementInspector.hide" defaultMessage="Hide" />
                </Button>
              ) : (
                <Button variant="primary" onClick={() => setDetailsOpen(true)}>
                  <FormattedMessage id="oneDElementInspector.show" defaultMessage="Show" />
                </Button>
              )
            }
          >
            <FormattedMessage id="oneDElementInspector.detailsHeader" defaultMessage="Details" />
          </PropertyTableHeader>

          <div
            className={`${assetInspectorStyles.DetailList}`}
            style={{
              display: detailsOpen ? "block" : "none",
            }}
          >
            {propertiesHidden.map((propertyInfo, index) => (
              <ShowPropertyInfo
                key={propertyInfo.property + index}
                asset={asset}
                propertyInfo={propertyInfo}
              />
            ))}
            <CrossSectionGraph asset={asset}></CrossSectionGraph>
          </div>
        </div>
      ) : null}

      {asset.properties.model_type === "pumps" ? (
        <TimeseriesPumpDischargeChart asset={asset} />
      ) : null}
      {isPotentialBreach(asset) ? <TimeseriesBreachCharts asset={asset} /> : null}
      {asset.geometry.type === "LineString" &&
      asset.properties.model_type !== "levees" &&
      !isPotentialBreach(asset) ? (
        <TimeseriesFlowChart asset={asset} />
      ) : null}
      {asset.geometry.type === "Point" &&
      asset.properties.model_type !== "pumps" &&
      asset.properties.model_type !== "breaches" ? (
        <>
          <TimeseriesChart asset={asset} data={waterTimeseriesGraph} type={"Waterlevel"} />
          {
            // hide waterdepth graph for nodes as they have no bottom_level
            // https://github.com/nens/threedi-live-site/issues/709
            asset.properties.model_type !== "nodes" ||
            (asset.properties.model_type === "nodes" &&
              typeof asset.properties.bottom_level === "number") ? (
              <TimeseriesChart
                asset={asset}
                data={getWaterdepthGraph(asset, waterTimeseriesGraph, demValue)}
                type={"WaterDepth"}
              />
            ) : null
          }
        </>
      ) : null}
    </div>
  );
};

export default SelectedAssetInspector;
